import { cloneElement, FC, isValidElement, useState } from "react";
import { MaterialReactTable, MRT_RowData, useMaterialReactTable } from "material-react-table";
import { useLocation, useNavigate } from "react-router-dom";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { Tooltip, Typography } from "@mui/material";

interface TableProps extends MRT_RowData {
  columns: any[];
  data: any[];
  onPaginationChange?: (...args: any) => void;
  state?: any;
}

export function paramsToObject(entries: any) {
  const result: any = {};
  for (const [key, value] of entries) {
    result[key] = value;
  }
  return result;
}
export const useParams = () => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const [urlParams, setUrlParams] = useState(new URLSearchParams(location.search));
  const entries = urlParams.entries();
  const params = paramsToObject(entries);
  const updateParams = (key: string, value: string) => {
    const p = urlParams;
    p.set(key, value);
    setUrlParams(p);
    navigate(`?${p.toString()}`);
  };
  return { pagination, params, setPagination, setUrlParams: updateParams };
};

export const OneLiner: FC<{ text: string; sx?: React.CSSProperties }> = ({ text, sx }) => {
  return (
    <Tooltip key={text} title={text} arrow>
      <Typography
        noWrap
        sx={() => ({
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: "pointer",
          ...sx,
        })}
      >
        {text}
      </Typography>
    </Tooltip>
  );
};

export const Table: FC<TableProps> = ({ columns, data, state, loading, muiTableBodyRowProps, initialState, ...rest }) => {
  const table = useMaterialReactTable({
    columns,
    //must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    data,
    //enable some features
    enableColumnResizing: true,
    enableColumnActions: true,
    enableRowActions: true,
    enableHiding: true,
    positionActionsColumn: "first",
    displayColumnDefOptions: {
      "mrt-row-actions": { header: "" },
    },
    initialState: {
      showGlobalFilter: true, // Make search box always visible
      ...initialState,
    },
    state: {
      ...state,
      isLoading: loading,
      columnPinning: { right: ["action"] },
    },
    muiTableBodyCellProps: {
      sx: {
        minHeight: "56px",
        paddingTop: 0,
        paddingBottom: 0,
        boxShadow: "none",
        backgroundColor: "inherit",
      },
    },
    muiTableBodyRowProps: (args: any) => ({
      hover: false,
      sx: (theme: any) => ({
        ...(typeof muiTableBodyRowProps?.(args)?.sx === "function"
          ? muiTableBodyRowProps?.(args)?.sx(theme)
          : muiTableBodyRowProps?.(args)?.sx),
        "&:hover": {
          backgroundColor: `${theme.palette.primary.main} !important`,
          "& .MuiCheckbox-root svg": {
            color: theme.palette.primary.contrastText,
          },
        },
        "&.Mui-selected": {
          "& .MuiCheckbox-root svg": {
            color: theme.palette.primary.main,
          },
          "&:hover .MuiCheckbox-root svg": {
            color: theme.palette.primary.contrastText,
          },
        },
      }),
      ...muiTableBodyRowProps?.(args),
    }),
    // Customise column action menu items:
    // - "Clear sort": disabled when column has no active sort (MRT enables it by default)
    // - "Clear filter": removed because MRT's internal debounced input doesn't sync when
    //   cleared externally — the X button on the filter input works correctly instead
    renderColumnActionsMenuItems: ({ column, internalColumnMenuItems }) => {
      return internalColumnMenuItems
        .filter(menuItem => {
          if (!isValidElement(menuItem)) return true;
          return (menuItem.props as any)?.label !== "Clear filter";
        })
        .map(menuItem => {
          if (!isValidElement(menuItem)) return menuItem;
          const label = (menuItem.props as any)?.label;
          if (label === "Clear sort") {
            return cloneElement(menuItem as React.ReactElement<any>, {
              disabled: !column.getIsSorted(),
            });
          }
          return menuItem;
        });
    },
    ...rest,
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MaterialReactTable table={table} />
    </LocalizationProvider>
  );
};
